import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCircleCheck, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { Button, Modal } from '@ui';
import type { PartnerLogEntry } from '@hooks/requests/queries/use_partner_logs_query';
import { formatLogTimestamp } from './dateUtils';

/** How long the copy button shows its "Copied" state before reverting. */
const COPIED_RESET_MS = 2000;

interface LogEventDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The row that was clicked. `null` is treated the same as `isOpen=false` (nothing to render). */
  entry: PartnerLogEntry | null;
}

// ── JSON syntax highlighting (Context panel) ───────────────────────────────────
// A line-based highlighter, not a full JSON AST walk — enough to reproduce
// the design's "keys/punctuation in gray, string/number values in red"
// look (Figma node 2771:10346) without pulling in a syntax-highlighting
// dependency for one read-only panel. Falls back to all-gray for any line
// shape it doesn't recognize (e.g. a bare array element), which is a safe
// default, not a broken one.
// Trailing commas are stripped from every rendered line (both the
// key/value case and the structural fallback below — a `},`/`],` closing a
// non-last property) to match the reference example's comma-free look.
// Display-only: "Copy context" below still copies a plain
// `JSON.stringify`, so the clipboard text stays valid, parseable JSON even
// though the on-screen rendering drops the separators.
const JSON_INDENT_PX_PER_LEVEL = 8; // 2 spaces (one JSON.stringify level) -> 16px, matching Figma's pl-[16px]/pl-[32px] steps.
const KEY_VALUE_LINE = /^(\s*)"([^"]*)":\s*(.*)$/;

const stripTrailingComma = (text: string): string =>
  text.endsWith(',') ? text.slice(0, -1) : text;

const renderJsonValueTail = (rest: string): React.ReactNode => {
  const value = stripTrailingComma(rest);
  // An opening brace/bracket (nested object/array) is structural, not a
  // value — keep it the same gray as keys and punctuation.
  const isStructural = value === '{' || value === '[' || value === '';
  return <span className={isStructural ? 'ledm-json-line' : 'ledm-json-value'}>{value}</span>;
};

/**
 * Renames a legacy `body_params` key to `request_payload` for display.
 *
 * The ally backend renamed this metadata field (specs/partner-api-logs.md
 * BR-002 follow-up, naming consistency with request_method/request_path/
 * response_status elsewhere) — but the rename only applies going forward:
 * rows already written to Firestore before it shipped still carry the old
 * key verbatim, and there's no migration rewriting historical rows. This
 * keeps the Context panel reading the same regardless of when the entry
 * was logged, rather than showing `body_params` for old rows and
 * `request_payload` for new ones. Key order is preserved (an in-place
 * rename, not a delete + re-add) so a renamed row's Context panel matches
 * the shape a freshly-logged one would have.
 */
const normalizeLegacyMetadataKeys = (
  metadata: Record<string, unknown>
): Record<string, unknown> => {
  if (!('body_params' in metadata) || 'request_payload' in metadata) return metadata;
  return Object.fromEntries(
    Object.entries(metadata).map(([key, value]) => [
      key === 'body_params' ? 'request_payload' : key,
      value,
    ])
  );
};

const JsonHighlighted: React.FC<{ value: unknown }> = ({ value }) => {
  const lines = JSON.stringify(value, null, 2).split('\n');
  return (
    <>
      {lines.map((line, index) => {
        const match = line.match(KEY_VALUE_LINE);
        if (!match) {
          return (
            <div key={index} className="ledm-json-line">
              {stripTrailingComma(line) || ' '}
            </div>
          );
        }
        const [, indent, key, rest] = match;
        // Nesting depth is unbounded, so the per-line indent can't be a
        // fixed CSS class — this is the one style that has to stay inline.
        return (
          <div key={index} style={{ paddingLeft: indent.length * JSON_INDENT_PX_PER_LEVEL }}>
            <span className="ledm-json-line">&quot;{key}&quot;: </span>
            {renderJsonValueTail(rest)}
          </div>
        );
      })}
    </>
  );
};

/**
 * Event details modal (sc-44238 State 2, Figma node 2771:10330): Date /
 * Request / Context, with a collapsible Context panel and a
 * copy-to-clipboard button that cycles Default -> Hover (CSS only) ->
 * Copied (this component's state) and reverts on its own.
 *
 * Built on the shared `Modal` shell but with its own header/footer
 * (`title`/`showCloseButton` left unset, `bodyClassName=""` to cancel
 * Modal's default `p-4`) — the design's header band and pill-shaped Close
 * button don't match any other modal in this app, so they're styled here
 * rather than added as new variants to the shared component.
 *
 * "Request" renders the same composed Details text as the table column
 * (`entry.details`) — the backend doesn't expose separate method/endpoint
 * fields on the wire, and reusing `details` keeps the two views from ever
 * disagreeing. "Context" renders `entry.metadata` (path/query params the
 * backend captured for this request), normalized through
 * `normalizeLegacyMetadataKeys` first, as syntax-highlighted JSON.
 */
const LogEventDetailsModal: React.FC<LogEventDetailsModalProps> = ({ isOpen, onClose, entry }) => {
  const [copied, setCopied] = useState(false);
  const [contextOpen, setContextOpen] = useState(true);

  if (!entry) return null;

  const displayMetadata = normalizeLegacyMetadataKeys(entry.metadata ?? {});
  const contextText = JSON.stringify(displayMetadata, null, 2);

  const handleCopyContext = async () => {
    try {
      await navigator.clipboard.writeText(contextText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      // Clipboard access can be denied (permissions, insecure context, etc.)
      // — fail silently rather than surface a toast for a "nice to have".
    }
  };

  return (
    // DS modal chrome shared with the API Keys / Team modals
    // (`.api-keys-modal*` in pages/api_keys.css); `.ledm-modal` only widens
    // it, since the Context JSON needs more room than a form modal.
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Event details"
      headerVariant="tinted"
      size="xl"
      className="api-keys-modal ledm-modal"
      bodyClassName="api-keys-modal__body"
      footerClassName="api-keys-modal__foot"
      footer={
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" className="au-modal-pill-btn--cancel" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {/* Content */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <p className="ledm-section-label">Date</p>
          <p className="ledm-date-value">{formatLogTimestamp(entry.timestamp)}</p>
        </div>

        <div className="flex flex-col gap-2 w-full">
          <p className="ledm-section-label">Request</p>
          <div className="ledm-panel-box w-full rounded-au-base px-4 py-1.5">
            <p className="ledm-request-text break-all">{entry.details}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 w-full">
          <button
            type="button"
            className="flex items-center justify-between w-full"
            onClick={() => setContextOpen((open) => !open)}
            aria-expanded={contextOpen}
          >
            <span className="ledm-section-label">Context</span>
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`ledm-context-chevron ${!contextOpen ? 'ledm-context-chevron--closed' : ''}`}
            />
          </button>

          {contextOpen && (
            <div className="ledm-panel-box w-full rounded-au-base p-4 flex flex-col gap-1">
              <div className="flex items-center justify-between w-full pb-1">
                <span className="ledm-json-label">JSON</span>
                <button
                  type="button"
                  onClick={handleCopyContext}
                  aria-label="Copy context"
                  className={`ledm-copy-btn inline-flex items-center justify-center gap-1.5 hover:opacity-70 transition-opacity ${
                    copied ? 'ledm-copy-btn--copied' : ''
                  }`}
                >
                  <FontAwesomeIcon icon={copied ? faCircleCheck : faCopy} />
                  {copied && 'Copied'}
                </button>
              </div>
              {/* A `<div>`, not `<pre>` — each JSON line is already its own
                  block-level `<div>` from JsonHighlighted, so `<pre>` would
                  nest block content inside phrasing content. `pre-wrap`
                  here gets the same "don't collapse whitespace, do wrap
                  long lines" behavior without that. */}
              {/* Figma's Context panel uses the app's default sans font
                  (Rubik) at 13px, not the mono font — only "Request" above
                  is monospace. */}
              <div className="ledm-json-content max-h-64 overflow-auto break-all">
                <JsonHighlighted value={displayMetadata} />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LogEventDetailsModal;
